import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@modules/auth';
import { QuoteItemOpMenu } from '@modules/quote/components/QuoteItemOpMenu';
import { useUnit } from '@modules/master/hooks/useUnit';
import { fetchEmployees } from '@modules/master';
import {
  fetchSubcontractors,
  fetchSubcontractOrder,
  createSubcontractOrder,
  createSubcontractor,
  updateSubcontractOrder,
  type SubcontractOrderCreateInput,
} from '../services/subcontract.service';
import { SubcontractorFormModal } from './SubcontractorFormModal';
import type { Subcontractor } from '../types';
import type { TaxCalcMode, TaxRate, LineItem as PersistedLineItem, EmbeddedFormConfig, Employee } from '@shared/types';
import { Button, OrderNoInput, SelectMenu, AiReviewSection } from '@shared/components';
import { nextDocumentBranchNumber, toHalfWidth, findIncompleteLineItemIndex, incompleteLineItemMessage } from '@shared/utils';

/** AIによる変更箇所パネルでフィールド名を日本語表示するためのラベル。 */
const SUBCONTRACT_ORDER_FIELD_LABELS: Record<string, string> = {
  subcontractorId: '外注先',
  orderId: '案件',
  assignedUserId: '担当者',
  title: '件名',
  description: '内容',
  orderDate: '発注日',
  deliveryDate: '納品予定日',
  amount: '金額',
  taxAmount: '消費税',
  total: '合計（税込）',
  status: 'ステータス',
  lineItems: '明細',
  subtotal: '小計',
  notes: '備考',
};

// ─── スタイル定数 ──────────────────────────────────────────────────────────────
const cls = 'w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';
const readCls = 'w-full border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50 text-gray-600 min-h-[28px]';
const LBL = 'bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 border-r border-gray-100 whitespace-nowrap align-middle';
const VAL = 'px-2 py-1.5 align-middle';
const TH = 'py-2 px-3 text-left text-xs font-semibold text-gray-600 border-b border-gray-200';

// ─── 明細行型 ─────────────────────────────────────────────────────────────────
interface LineItem {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxType: 'standard' | 'reduced' | 'exempt';
  sortOrder: number;
}

type TaxType = 'standard' | 'reduced' | 'exempt';

// 見積明細と同じ税率（非課税）プルダウンの選択肢・表示ラベル
const TAX_OPTIONS: { value: TaxType; label: string }[] = [
  { value: 'standard', label: '10%' },
  { value: 'reduced', label: '8%' },
  { value: 'exempt', label: '非課税' },
];
const taxLabel = (t: TaxType): string => TAX_OPTIONS.find((o) => o.value === t)?.label ?? '';

/** 入力欄右端に表示する下向きシェブロン（単位・税率の選択ボタン共通。見積明細と統一）。 */
function ChevronDown() {
  return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}

// 外注注文テーブルのデフォルト表示行数（見積書と同じ仕様。最低この行数を空行で埋める）
const MIN_ITEM_ROWS = 15;

const INIT_ITEMS: LineItem[] = Array.from({ length: MIN_ITEM_ROWS }, (_, i) => ({
  name: '', quantity: 0, unit: '', unitPrice: 0, taxType: 'standard' as TaxType, sortOrder: i,
}));

// 読み込んだ明細を最低 MIN_ITEM_ROWS 行まで空行でパディングする。
const padItems = (items: LineItem[]): LineItem[] => {
  if (items.length >= MIN_ITEM_ROWS) return items;
  const fillers: LineItem[] = Array.from({ length: MIN_ITEM_ROWS - items.length }, (_, i) => ({
    name: '', quantity: 0, unit: '', unitPrice: 0, taxType: 'standard' as TaxType, sortOrder: items.length + i,
  }));
  return [...items, ...fillers];
};

const NEW_ITEM: LineItem = { name: '', quantity: 0, unit: '', unitPrice: 0, taxType: 'standard', sortOrder: 0 };

// 行に何らかの入力があるか（品名・数量・単位・単価のいずれか）。
function isRowFilled(it: LineItem): boolean {
  return it.name.trim() !== '' || it.quantity > 0 || it.unit.trim() !== '' || it.unitPrice !== 0;
}

// ─── ユーティリティ ───────────────────────────────────────────────────────────
function yen(n: number): string { return n.toLocaleString() + '円'; }

// 自由入力（スピナーなし）の数値パース。
// 見積書・仕入管理と同じく全角入力を受け付け、toHalfWidth で半角へ正規化してから
// 数字と小数点のみを許容し、不正値は0に丸める。
function parseNum(s: string): number {
  const n = Number(toHalfWidth(s).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

// 単価は値引き（マイナス）入力を許容する。小数・空文字・入力途中の "-" にも対応。
function parsePrice(s: string): number {
  if (s === '' || s === '-') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
const priceVal = (v: number): string => (v === 0 ? '' : String(v));

function toStr(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return '';
  return ts.toDate().toISOString().split('T')[0];
}

function fromStr(s: string): Timestamp | null {
  return s ? Timestamp.fromDate(new Date(s)) : null;
}

// 明細行の税区分 ⇄ 永続化用の税率を相互変換する
function taxTypeToRate(t: TaxType): TaxRate {
  return t === 'standard' ? 10 : t === 'reduced' ? 8 : 'exempt';
}
function rateToTaxType(r: TaxRate): TaxType {
  return r === 10 ? 'standard' : r === 8 ? 'reduced' : 'exempt';
}

function roundByMode(value: number, mode: TaxCalcMode): number {
  if (mode === 'ceil') return Math.ceil(value);
  if (mode === 'round') return Math.round(value);
  return Math.floor(value);
}

interface Totals { subtotal: number; taxAmount: number; exempt: number; grandTotal: number; }

function calcTotals(items: LineItem[], mode: TaxCalcMode): Totals {
  let subtotal = 0; let tax = 0; let exempt = 0;
  for (const item of items) {
    const s = item.unitPrice * item.quantity;
    subtotal += s;
    if (item.taxType === 'standard') tax += roundByMode(s * 0.1, mode);
    else if (item.taxType === 'reduced') tax += roundByMode(s * 0.08, mode);
    else exempt += s;
  }
  return { subtotal, taxAmount: tax, exempt, grandTotal: subtotal + tax };
}

// ─── SectionBox ──────────────────────────────────────────────────────────────
interface SectionBoxProps { title: string; children: React.ReactNode; }
function SectionBox({ title, children }: SectionBoxProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
        <p className="text-sm font-semibold text-gray-600">{title}</p>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

// ─── Field（縦積み）──────────────────────────────────────────────────────────
interface FieldProps { label: string; required?: boolean; children: React.ReactNode; }
function Field({ label, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── 明細行コンポーネント ──────────────────────────────────────────────────────
interface ItemRowProps {
  item: LineItem;
  index: number;
  total: number;
  unitOptions: string[];
  onChange: (i: number, p: Partial<LineItem>) => void;
  onInsertAbove: (i: number) => void;
  onSwapUp: (i: number) => void;
  onSwapDown: (i: number) => void;
  onDeleteMultiple: (i: number, count: number) => void;
  onDelete: (i: number) => void;
}
function ItemRow({
  item, index, total, unitOptions, onChange,
  onInsertAbove, onSwapUp, onSwapDown, onDeleteMultiple, onDelete,
}: ItemRowProps) {
  const subtotal = item.unitPrice * item.quantity;
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [showTaxPicker, setShowTaxPicker] = useState(false);
  const unitRef = useRef<HTMLDivElement>(null);
  const taxRef = useRef<HTMLDivElement>(null);

  // 単価入力の表示文字列（マイナス入力途中の "-" を保持するためローカル管理）。
  const [priceText, setPriceText] = useState<string>(() => priceVal(item.unitPrice));
  useEffect(() => {
    if (parsePrice(priceText) !== item.unitPrice) setPriceText(priceVal(item.unitPrice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.unitPrice]);

  useEffect(() => {
    if (!showUnitPicker && !showTaxPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (unitRef.current && !unitRef.current.contains(e.target as Node)) setShowUnitPicker(false);
      if (taxRef.current && !taxRef.current.contains(e.target as Node)) setShowTaxPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUnitPicker, showTaxPicker]);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-1"><input value={item.name} onChange={(e) => onChange(index, { name: e.target.value })} className={cls} placeholder="" /></td>
      <td className="px-2 py-1 w-24"><input type="text" inputMode="numeric" value={item.quantity || ''} onChange={(e) => onChange(index, { quantity: parseNum(e.target.value) })} className={cls + ' text-right'} /></td>
      <td className="px-2 py-1 w-20">
        <div ref={unitRef} className="relative">
          <input value={item.unit} onChange={(e) => onChange(index, { unit: e.target.value })} placeholder="" className={cls + ' pr-6'} />
          <button type="button" onClick={() => setShowUnitPicker((v) => !v)} title="単位を選択" className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"><ChevronDown /></button>
          {showUnitPicker && (
            <SelectMenu
              items={unitOptions.map((u) => ({
                key: u,
                label: u,
                onSelect: () => { onChange(index, { unit: u }); setShowUnitPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="px-2 py-1 w-32"><input type="text" inputMode="decimal" value={priceText} onChange={(e) => { const raw = toHalfWidth(e.target.value).replace(/[^\d.-]/g, '').replace(/(?!^)-/g, ''); setPriceText(raw); onChange(index, { unitPrice: parsePrice(raw) }); }} className={cls + ' text-right'} /></td>
      <td className="px-2 py-1 w-32"><div className="border border-gray-300 rounded px-2 py-1 text-sm text-right text-gray-700 tabular-nums bg-gray-50">{subtotal !== 0 ? subtotal.toLocaleString() : '—'}</div></td>
      <td className="px-2 py-1 w-28">
        <div ref={taxRef} className="relative">
          <div onClick={() => setShowTaxPicker((v) => !v)} className={cls + ' pr-6 bg-white cursor-pointer select-none flex items-center'}>{taxLabel(item.taxType)}</div>
          <button type="button" onClick={() => setShowTaxPicker((v) => !v)} title="税率を選択" className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"><ChevronDown /></button>
          {showTaxPicker && (
            <SelectMenu
              items={TAX_OPTIONS.map((o) => ({
                key: o.value,
                label: o.label,
                onSelect: () => { onChange(index, { taxType: o.value }); setShowTaxPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="px-2 py-1 w-14 text-center">
        <QuoteItemOpMenu index={index} total={total} onInsertAbove={() => onInsertAbove(index)} onSwapUp={() => onSwapUp(index)} onSwapDown={() => onSwapDown(index)} onDeleteMultiple={(count) => onDeleteMultiple(index, count)} onDeleteRow={() => onDelete(index)} />
      </td>
    </tr>
  );
}

// ─── フォームデータ型 ─────────────────────────────────────────────────────────
interface FormData {
  subcontractorId: string;
  orderId: string;
  title: string;
  description: string;
  orderDate: Timestamp;
  deliveryDate: Timestamp | null;
  assignedUserId: string;
  notes: string;
  taxCalcMode: TaxCalcMode;
}

// ─── SubcontractOrderFormPage ──────────────────────────────────────────────────
export function SubcontractOrderFormPage({ embedded }: { embedded?: EmbeddedFormConfig } = {}) {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // 埋め込み表示時は ID・state・遷移をすべて embedded から取得する
  const id = embedded ? embedded.id : params.id;
  const navState = embedded ? embedded.state : location.state;
  const isEdit = Boolean(id);

  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [form, setForm] = useState<FormData>({
    subcontractorId: '',
    orderId: '',
    title: '',
    description: '',
    orderDate: Timestamp.now(),
    deliveryDate: null,
    assignedUserId: '',
    notes: '',
    taxCalcMode: 'floor',
  });
  const [items, setItems] = useState<LineItem[]>(INIT_ITEMS);
  const [docNo, setDocNo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewSubcontractor, setShowNewSubcontractor] = useState(false);
  // 編集対象が「AIレビュー待ち」か（form は FormData のためレコードの印は別stateで保持）
  const [aiPending, setAiPending] = useState(false);

  const upd = useCallback((patch: Partial<FormData>) => setForm((f) => ({ ...f, ...patch })), []);

  const selectedSubcontractor = subcontractors.find((s) => s.id === form.subcontractorId) ?? null;

  // 案件詳細から遷移した場合は受注IDを初期表示する
  useEffect(() => {
    if (id) return;
    const st = navState as { orderId?: string } | null;
    if (st?.orderId) upd({ orderId: st.orderId });
  }, [id, navState, upd]);

  // 外注先一覧ロード
  useEffect(() => {
    void fetchSubcontractors().then(setSubcontractors).catch(() => setSubcontractors([]));
  }, []);

  // 編集時データロード
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void fetchSubcontractOrder(id)
      .then((o) => {
        if (!o) return;
        setDocNo(o.code ?? null);
        setAiPending(o.aiPendingReview === true);
        setForm({
          subcontractorId: o.subcontractorId,
          orderId: o.orderId ?? '',
          title: o.title,
          description: o.description ?? '',
          orderDate: o.orderDate,
          deliveryDate: o.deliveryDate ?? null,
          assignedUserId: o.assignedUserId ?? '',
          notes: o.notes ?? '',
          taxCalcMode: o.taxCalcMode ?? 'floor',
        });
        const loaded: LineItem[] = (o.lineItems ?? []).map((li, i) => ({
          name: li.title,
          quantity: li.qty,
          unit: li.unitId,
          unitPrice: li.unitPrice,
          taxType: rateToTaxType(li.taxRate),
          sortOrder: i,
        }));
        setItems(loaded.length ? padItems(loaded) : INIT_ITEMS);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : '読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, [id]);

  // 新規作成（案件紐付けあり）の場合、採番予定の枝番をプレビュー表示する
  useEffect(() => {
    if (id) return;
    if (!form.orderId) { setDocNo(null); return; }
    let active = true;
    void nextDocumentBranchNumber('subcontract_orders', form.orderId)
      .then((n) => { if (active) setDocNo(n); })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : '関連文書の取得に失敗しました。');
      });
    return () => { active = false; };
  }, [id, form.orderId]);

  const { subtotal, taxAmount, exempt, grandTotal } = calcTotals(items, form.taxCalcMode);

  // 単位マスタ（マスター管理 → 単位）を読み込み、明細の単位候補に反映する（見積明細と同一）
  const { units, loadUnits } = useUnit();
  useEffect(() => { void loadUnits(); }, [loadUnits]);
  useEffect(() => { void fetchEmployees().then(setEmployees).catch(() => setEmployees([])); }, []);
  const unitOptions = useMemo(
    () => [...units].sort((a, b) => a.priority - b.priority).map((u) => u.name),
    [units],
  );

  const handleItemChange = useCallback((index: number, patch: Partial<LineItem>) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }, []);

  const handleItemDelete = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index).map((it, i) => ({ ...it, sortOrder: i })));
  }, []);

  // 見積明細と同じ操作メニュー（行の挿入・並べ替え・複数行消去）
  const handleInsertAbove = useCallback((index: number) => {
    setItems((prev) => {
      const next = [...prev];
      next.splice(index, 0, { ...NEW_ITEM });
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);
  const handleSwap = useCallback((a: number, b: number) => {
    setItems((prev) => {
      if (a < 0 || b < 0 || a >= prev.length || b >= prev.length) return prev;
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);
  const handleDeleteMultiple = useCallback((index: number, count: number) => {
    setItems((prev) => {
      const next = [...prev];
      next.splice(index, Math.max(1, count));
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setItems((prev) => [...prev, { ...NEW_ITEM, sortOrder: prev.length }]);
  }, []);

  const handleSave = async () => {
    if (!user) { setError('ログインが必要です'); return; }
    if (!form.subcontractorId) { setError('外注先は必須です'); return; }
    if (!form.title.trim()) { setError('件名は必須です'); return; }
    // 数量・単位・単価のいずれかが入力された行のみ必須チェック（品名のみのフリー記載行は許可）
    const invalidIndex = findIncompleteLineItemIndex(items);
    if (invalidIndex >= 0) { setError(incompleteLineItemMessage(invalidIndex)); return; }
    setSaving(true);
    setError(null);
    try {
      // ユーザーが入力した行の位置を保持するため、途中の空行（スペーサー行）は詰めずに
      // そのまま保存し、末尾の空行（MIN_ITEM_ROWS の余白行）のみ除外する。
      let lastFilled = -1;
      items.forEach((it, i) => { if (isRowFilled(it)) lastFilled = i; });
      const lineItems: PersistedLineItem[] = items
        .slice(0, lastFilled + 1)
        .map((it, idx) => ({
          id: 'li-' + (idx + 1),
          title: it.name,
          qty: it.quantity,
          unitId: it.unit,
          unitPrice: it.unitPrice,
          amount: it.unitPrice * it.quantity,
          taxRate: taxTypeToRate(it.taxType),
        }));
      const input: SubcontractOrderCreateInput = {
        subcontractorId: form.subcontractorId,
        orderId: form.orderId || null,
        assignedUserId: form.assignedUserId || null,
        title: form.title,
        description: form.description || null,
        amount: subtotal,
        taxAmount,
        total: grandTotal,
        status: 'draft',
        orderDate: form.orderDate,
        deliveryDate: form.deliveryDate,
        paidAt: null,
        notes: form.notes || null,
        billId: null,
        paymentDate: null,
        lineItems,
        subtotal,
        totalgst: taxAmount,
        grandtotal: grandTotal,
        taxCalcMode: form.taxCalcMode,
        isZeroSale: false,
      };
      if (isEdit && id) {
        await updateSubcontractOrder(id, input, user.id);
        if (embedded) { embedded.onSaved(); return; }
        navigate('/subcontracts/' + id + '/edit');
      } else {
        const created = await createSubcontractOrder(input, user.id);
        if (embedded) { embedded.onSaved(); return; }
        navigate('/subcontracts/' + created.id + '/edit');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (embedded) { embedded.onCancel(); return; }
    navigate('/subcontracts');
  };

  const handleCreateSubcontractor = async (data: Parameters<typeof createSubcontractor>[0]) => {
    if (!user) throw new Error('ログインが必要です');
    const created = await createSubcontractor(data, user.id);
    const list = await fetchSubcontractors();
    setSubcontractors(list);
    upd({ subcontractorId: created.id });
  };

  if (loading) return <div className="p-6 text-gray-500">読み込み中...</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'}>
      {!embedded && (
        <>
          <div className="flex items-baseline gap-8">
            <h1 className="text-xl font-bold text-gray-800">外注管理</h1>
            {docNo && <span className="text-base font-semibold text-gray-500">#{docNo}</span>}
          </div>

          <nav className="text-xs text-gray-500 flex items-center gap-1">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span>/</span>
            <Link to="/subcontracts" className="hover:text-blue-600">外注管理</Link>
            <span>/</span>
            <span className="text-gray-700">{isEdit ? '外注発注編集' : '外注発注作成'}</span>
          </nav>
        </>
      )}

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
      )}

      {/* AIレビュー待ちの外注発注: 変更箇所の表示と「レビュー済みにする」（設計書 §3.5） */}
      {isEdit && id && (
        <AiReviewSection
          entityType="subcontract_order"
          collectionName="subcontract_orders"
          entityId={id}
          pending={aiPending}
          userId={user?.id}
          fieldLabels={SUBCONTRACT_ORDER_FIELD_LABELS}
          onConfirmed={() => setAiPending(false)}
        />
      )}

      {/* 基本情報 */}
      <SectionBox title="基本情報">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="外注先" required>
            <div className="flex gap-2">
              <select value={form.subcontractorId} onChange={(e) => upd({ subcontractorId: e.target.value })} className={cls} required>
                <option value="">選択してください</option>
                {subcontractors.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowNewSubcontractor(true)}>
                ＋新規登録
              </Button>
            </div>
          </Field>
          <Field label="件名" required>
            <input value={form.title} onChange={(e) => upd({ title: e.target.value })} className={cls} placeholder="件名" required />
          </Field>
          <Field label="発行日" required>
            <input type="date" value={toStr(form.orderDate)} onChange={(e) => upd({ orderDate: fromStr(e.target.value) ?? Timestamp.now() })} className={cls} />
          </Field>
          <Field label="納品予定日">
            <input type="date" value={toStr(form.deliveryDate)} onChange={(e) => upd({ deliveryDate: fromStr(e.target.value) })} className={cls} />
          </Field>
          <Field label="担当者">
            <select value={form.assignedUserId} onChange={(e) => upd({ assignedUserId: e.target.value })} className={cls}>
              <option value="">選択してください</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </Field>
          <Field label="関連受注番号">
            <OrderNoInput orderId={form.orderId} onChange={(v) => upd({ orderId: v })} className={cls} />
          </Field>
        </div>
      </SectionBox>

      {/* 外注先グリッド */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <p className="text-sm font-semibold text-gray-600">外注先</p>
        </div>
        <table className="w-full text-sm border-collapse table-fixed">
          <colgroup>
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
          </colgroup>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className={LBL + ' w-28'}>外注先名</td>
              <td className={VAL + ' w-44'}>
                <div className={readCls}>{selectedSubcontractor?.name ?? '—'}</div>
              </td>
              <td className={LBL + ' w-28'}>担当者</td>
              <td className={VAL + ' w-44'}>
                <div className={readCls}>—</div>
              </td>
              <td className={LBL + ' w-28'}>TEL</td>
              <td className={VAL + ' w-44'}>
                <div className={readCls}>{selectedSubcontractor?.phone ?? '—'}</div>
              </td>
              <td className={LBL + ' w-24'}>コード</td>
              <td className={VAL}>
                <div className={readCls}>{selectedSubcontractor?.code ?? '—'}</div>
              </td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className={LBL}>〒</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.postalCode ?? '—'}</div></td>
              <td className={LBL}>都道府県</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.prefecture || '—'}</div></td>
              <td className={LBL}>住所（市区町村）</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.addr1 || '—'}</div></td>
              <td className={LBL}>住所（番地）</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.addr2 || '—'}</div></td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className={LBL}>住所（建物名・フロア）</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.addr3 || '—'}</div></td>
              <td className={LBL}>部署</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor?.department || '—'}</div></td>
              <td className={LBL}>支払期日</td>
              <td className={VAL}><div className={readCls}>{selectedSubcontractor ? selectedSubcontractor.paymentTerms + '日' : '—'}</div></td>
              <td className={LBL}></td>
              <td className={VAL}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 明細テーブル */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <p className="text-sm font-semibold text-gray-600">依頼明細</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className={TH}>品名・摘要</th>
                <th className={TH + ' w-24'}>数量</th>
                <th className={TH + ' w-20'}>単位</th>
                <th className={TH + ' w-32'}>単価</th>
                <th className={TH + ' w-32'}>税抜料金</th>
                <th className={TH + ' w-28'}>税率</th>
                <th className="py-2 px-3 w-12 border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <ItemRow
                  key={i}
                  item={item}
                  index={i}
                  total={items.length}
                  unitOptions={unitOptions}
                  onChange={handleItemChange}
                  onInsertAbove={handleInsertAbove}
                  onSwapUp={(idx) => handleSwap(idx, idx - 1)}
                  onSwapDown={(idx) => handleSwap(idx, idx + 1)}
                  onDeleteMultiple={handleDeleteMultiple}
                  onDelete={handleItemDelete}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* 合計エリア */}
        <div className="border-t border-gray-200 bg-white">
          <div className="flex justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm text-gray-700">小計</span>
            <span className="text-sm text-gray-900 tabular-nums">{yen(subtotal)}</span>
          </div>
          <div className="flex justify-between px-4 py-2 border-b border-gray-100 items-center">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-700">消費税額</span>
              <select value={form.taxCalcMode} onChange={(e) => upd({ taxCalcMode: e.target.value as TaxCalcMode })}
                className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="floor">切り捨て</option>
                <option value="ceil">切り上げ</option>
                <option value="round">四捨五入</option>
                <option value="manual">手動</option>
              </select>
            </div>
            <span className="text-sm text-gray-900 tabular-nums">{yen(taxAmount)}</span>
          </div>
          <div className="flex justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm text-gray-700">非課税税額</span>
            <span className="text-sm text-gray-900 tabular-nums">{yen(exempt)}</span>
          </div>
          <div className="flex justify-between px-4 py-2">
            <span className="text-sm font-bold text-gray-900">合計</span>
            <span className="text-sm font-bold text-gray-900 tabular-nums">{yen(grandTotal)}</span>
          </div>
        </div>

        {/* ADDボタン */}
        <div className="px-4 py-3 border-t border-gray-200">
          <Button size="sm" onClick={handleAddRow}>
            ADD
          </Button>
        </div>
      </div>

      {/* 備考 */}
      <SectionBox title="備考（メモ）">
        <textarea value={form.notes} onChange={(e) => upd({ notes: e.target.value })}
          rows={3} className={cls} placeholder="備考・メモを入力してください" />
      </SectionBox>

      {/* 保存/キャンセル */}
      <div className="flex justify-center gap-4 py-4">
        <Button variant="secondary" onClick={handleCancel}>
          キャンセル
        </Button>
        <Button disabled={saving} onClick={() => void handleSave()}>
          {saving ? '保存中...' : '保存'}
        </Button>
      </div>

      {showNewSubcontractor && (
        <SubcontractorFormModal
          isOpen
          onClose={() => setShowNewSubcontractor(false)}
          onSubmit={handleCreateSubcontractor}
        />
      )}
    </div>
  );
}
